import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { SnackbarV3Service } from '@gea/digital-ui-lib';
import { SelectOption } from '@gea/digital-ui-lib';
import { PpmProductResponse } from '../../pages/products/product.model';

@Injectable({
  providedIn: 'root',
})
export class UtilityService {
  constructor(
    private router: Router,
    private snackbarService: SnackbarV3Service
  ) {}

  getProductDisplayName(
    products: { id: string; productType: string; createdOn: string | Date }[],
    productId: string,
    productType: string
  ): string {
    if (products.length <= 1) {
      return productType;
    }

    const iterator = [...products]
      .sort((left, right) => {
        const createdOnDifference = new Date(left.createdOn).getTime() - new Date(right.createdOn).getTime();
        return createdOnDifference || left.id.localeCompare(right.id);
      })
      .findIndex((product) => product.id === productId);

    return iterator >= 0 ? `(${iterator + 1}) ${productType}` : productType;
  }

  openProjectReport(id: string) {
    this.router.navigate([`project-reports/${id}`]);
  }

  openProjectDetails(id: string): void {
    this.router.navigate([`project-details/${id}`]);
  }

  openProduct(id: string) {
    this.router.navigate(['/site-details', { id: id }]);
  }

  openAsset(assetId: string, repositoryKey: string): void {
    const route = [`/data-maintenance/${encodeURIComponent(assetId)}`];
    this.router.navigate(route, { queryParams: { repositoryKey } });
  }

  openConfigurator(projectId: string, productId: string, ppmResponse?: PpmProductResponse, displayLabel?: string): void {
    // Pass the full PpmProductResponse (from createProductInstance) in router state
    // so the configurator can bootstrap immediately without a second HTTP call.
    // TODO: Remove ppmResponse param once GET /products/{id} returns the new
    //       PpmProductResponse shape — the configurator will load via
    //       getProductInstance automatically at that point.
    if (displayLabel) {
      sessionStorage.setItem('productBreadcrumbLabel', displayLabel);
    } else {
      sessionStorage.removeItem('productBreadcrumbLabel');
    }
    this.router.navigate([`project-configurator/${projectId}/${productId}`], {
      state: ppmResponse ? { ppmProductResponse: ppmResponse } : undefined,
    });
  }

  isExitEventFromGEADialog(eventFromDialog: any): boolean {
    return typeof eventFromDialog === 'object' && 'buttonType' in eventFromDialog && eventFromDialog.buttonType === 'Exit';
  }

  isRejectEventFromGEADialog(eventFromDialog: any): boolean {
    return typeof eventFromDialog === 'object' && 'buttonType' in eventFromDialog && eventFromDialog.buttonType === 'Reject';
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    const d = this.formatTwoDigits(date.getDate());
    const m = this.formatTwoDigits(date.getMonth() + 1);
    const y = date.getFullYear();
    const h = this.formatTwoDigits(date.getHours());
    const min = this.formatTwoDigits(date.getMinutes());

    return `${d}.${m}.${y} ${h}:${min}`;
  }

  patternWithMessage(pattern: RegExp, message: string) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      return pattern.test(control.value) ? null : { custom: message };
    };
  }

  private formatTwoDigits(n: number): string {
    return n.toString().padStart(2, '0');
  }

  openSnackbar(summary: string, detail: string, severity: 'success' | 'info' | 'error'): void {
    this.snackbarService.add({
      summary,
      detail,
      severity,
    });
  }

  constructSelectOptions(options: string[]): SelectOption[] {
    const selectOptions: SelectOption[] = [];
    options.forEach((name: string, index: number) => {
      selectOptions.push({
        value: index + 1,
        name: name,
      });
    });
    return selectOptions;
  }
}
